import { useState, useEffect, useCallback } from 'react';
import { ServiceOffering, CreateServiceOfferingData, SearchFilters } from '@/types';
import {
  createServiceOffering,
  getAvailableServiceOfferings,
  getUserServiceOfferings,
  updateServiceOffering,
  searchServicesByCategory,
  searchServicesByPayment,
} from '@/services';
import { SERVICE_TYPES } from '@/constants';

export interface UseServicesReturn {
  services: ServiceOffering[];
  myServices: ServiceOffering[];
  loading: boolean;
  error: string | null;
  createOffering: (data: CreateServiceOfferingData) => Promise<string>;
  updateOffering: (offeringId: string, isActive: boolean) => Promise<void>;
  refreshServices: () => Promise<void>;
  searchServices: (filters: SearchFilters) => Promise<void>;
}

/**
 * Custom hook for service offerings
 */
export const useServices = (userId?: string): UseServicesReturn => {
  const [services, setServices] = useState<ServiceOffering[]>([]);
  const [myServices, setMyServices] = useState<ServiceOffering[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadServices = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [available, mine] = await Promise.all([
        getAvailableServiceOfferings(userId),
        getUserServiceOfferings(userId),
      ]);

      setServices(available);
      setMyServices(mine);
    } catch (err) {
      setError('Failed to load services');
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const createOffering = async (data: CreateServiceOfferingData): Promise<string> => {
    try {
      setError(null);
      const offeringId = await createServiceOffering(data);
      await loadServices();
      return offeringId;
    } catch (err) {
      setError('Failed to create offering');
      console.error('Error creating offering:', err);
      throw err;
    }
  };

  const updateOffering = async (offeringId: string, isActive: boolean): Promise<void> => {
    try {
      setError(null);
      await updateServiceOffering(offeringId, { isActive });
      await loadServices();
    } catch (err) {
      setError('Failed to update offering');
      console.error('Error updating offering:', err);
      throw err;
    }
  };

  const refreshServices = async (): Promise<void> => {
    await loadServices();
  };

  const searchServices = async (filters: SearchFilters): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      let results: ServiceOffering[] = [];

      if (filters.category) {
        results = await searchServicesByCategory(filters.category, userId);
      } else if (filters.paymentPreference) {
        results = await searchServicesByPayment(filters.paymentPreference, userId);
      } else {
        results = await getAvailableServiceOfferings(userId);
      }

      // Client-side search query filtering - search by name, description, and service type names
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        results = results.filter((service) => {
          // Check user name
          if (service.userName.toLowerCase().includes(query)) return true;

          // Check description
          if (service.description?.toLowerCase().includes(query)) return true;

          // Check service type names (both EN and FR)
          const serviceTypeNames = service.serviceTypes
            .map((typeId) => {
              const serviceType = SERVICE_TYPES.find((st) => st.id === typeId);
              if (serviceType) {
                return `${serviceType.name.en.toLowerCase()} ${serviceType.name.fr.toLowerCase()}`;
              }
              return '';
            })
            .join(' ');

          if (serviceTypeNames.includes(query)) return true;

          return false;
        });
      }

      setServices(results);
    } catch (err) {
      setError('Failed to search services');
      console.error('Error searching services:', err);
    } finally {
      setLoading(false);
    }
  };

  return {
    services,
    myServices,
    loading,
    error,
    createOffering,
    updateOffering,
    refreshServices,
    searchServices,
  };
};
